import React from 'react';
import FeeTypesEnum from '../../../Enums/FeeTypesEnum';
import InputTypesEnum from '../../../Enums/InputTypesEnum';
import DataSourceEnum from '../../../Enums/DataSourceEnum';
import LineItemTypeEnum from '../../../Enums/LineItemTypeEnum';
import ValidityHelper from '../../../Helpers/ValidityHelper';
import FreelancerLineItem from '../../../Domain/FreelancerLineItem';
import FreelancerLineItemService from '../../../Services/FreelancerLineItemService';

export default class FreelancerLineItemWindowDescription {
  static titleForCreate = 'Add New Line Item';
  static titleForEdit = (item) => `${item.FreelancerUserName} - Edit Line Item (${item.FreelancerLineItemId})`;
  static finishButtonLabel = 'Save Line Item';

  // Getter (not eager static): FreelancerLineItem <-> FreelancerUserAdmin form an import cycle, so
  // referencing FreelancerLineItem at class-definition time would throw a TDZ ReferenceError.
  static get itemType() {
    return FreelancerLineItem;
  }

  static messageOnSuccess = (edited) =>
    edited.FreelancerLineItemId ? 'Line item has been modified successfully.' : 'Line item has been added successfully.';

  // Handles both create and edit; the service PUTs to a create-or-update endpoint. Must return the promise.
  static serviceFunction = (edited) => FreelancerLineItemService.EditOrCreateFreelancerLineItem(edited);

  // Create path (FreelancerUserAdmin): build the line item from the already-loaded vendor, carrying the
  // role flags (Fee Type option list) and currency (fee label) onto it. Mirrors the old inner box's
  // create-time seeding (FeeType default, IsActive, LineItemType, Fee).
  static buildItemForUser(user) {
    const item = new FreelancerLineItem();
    item.FreelancerUserId = user.FreelancerUserId;
    item.FreelancerUserName = `${user.FirstName} ${user.LastName}`;
    item.FreelancerUserCurrencyCode = user.CurrencyCode;
    item.FeeType = user.IsRM ? FeeTypesEnum.Resource : user.IsDQ ? FeeTypesEnum.DataQuality : '';
    item.FreelancerUserIsRM = user.IsRM;
    item.FreelancerUserIsDQ = user.IsDQ;
    item.FreelancerUserIsKAM = user.IsKAM;
    item.FreelancerUserIsBD = user.IsBD;
    item.FreelancerUserIsBI = user.IsBI;
    item.FreelancerUserIsIT = user.IsIT;
    item.FreelancerUserIsHR = user.IsHR;
    item.FreelancerUserIsMarketing = user.IsMarketing;
    item.FreelancerUserIsFinance = user.IsFinance;
    item.FreelancerUserIsFieldworkPartner = user.IsFieldworkPartner;
    item.IsActive = true;
    item.LineItemType = LineItemTypeEnum.Addition;
    item.Fee = 0;
    return item;
  }

  static isTimesheet = (item) => item.TimesheetEntryId > 0;
  static isThirdParty = (item) => item.Source === DataSourceEnum.ThirdParty;

  static pagesInGeneralEditWizzard = [
    {
      label: 'General',
      fields: [
        {
          fieldName: 'FreelancerUserId',
          label: 'Vendor Id',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FreelancerUserName',
          label: 'Vendor Name',
          className: 'col-12',
          type: InputTypesEnum.TextReadOnly,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'FeeType',
          label: 'Fee Type',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: (item) => FreelancerLineItemWindowDescription.getFeeTypeValues(item),
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          validityChecker: ValidityHelper.forRequiredSelectField,
        },
        {
          fieldName: 'LineItemType',
          label: 'Line Item Type',
          className: 'col-12',
          type: InputTypesEnum.Select,
          selectvalues: [
            { label: LineItemTypeEnum.Addition, value: LineItemTypeEnum.Addition, disabled: false },
            { label: LineItemTypeEnum.Deduction, value: LineItemTypeEnum.Deduction, disabled: false },
          ],
          disableClearButton: true,
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          validityChecker: ValidityHelper.AlwaysFlawless,
        },
        {
          fieldName: 'ClientId',
          label: 'Client',
          className: 'col-12',
          type: InputTypesEnum.ClientSelect,
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          validityChecker: (e, item) =>
            item.FreelancerUserIsFieldworkPartner ? ValidityHelper.forRequiredSelectField(e) : '',
          onFieldChange: FreelancerLineItemWindowDescription.onClientChange,
        },
        {
          fieldName: 'Source',
          label: 'Source',
          className: 'col-12',
          type: InputTypesEnum.SourceSelect,
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          onFieldChange: FreelancerLineItemWindowDescription.onSourceChange,
        },
        {
          fieldName: 'SurveyId',
          label: 'Survey',
          className: 'col-12',
          type: InputTypesEnum.SurveySelect,
          hide: (item) =>
            FreelancerLineItemWindowDescription.isTimesheet(item) ||
            FreelancerLineItemWindowDescription.isThirdParty(item),
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'TpSurveyId',
          label: 'TpSurvey',
          className: 'col-12',
          type: InputTypesEnum.TpSurveySelect,
          hide: (item) =>
            FreelancerLineItemWindowDescription.isTimesheet(item) ||
            !FreelancerLineItemWindowDescription.isThirdParty(item),
          disabled: (item) => !item.ClientId,
          idFilter: 'ClientId',
        },
        {
          fieldName: 'WaveId',
          label: 'Wave',
          className: 'col-12',
          type: InputTypesEnum.WaveSelect,
          hide: (item) =>
            FreelancerLineItemWindowDescription.isTimesheet(item) ||
            FreelancerLineItemWindowDescription.isThirdParty(item),
          disabled: (item) => !(parseInt(item.ClientId) > 0),
          idFilter: 'ClientId',
        },
        {
          fieldName: 'ApplyDate',
          label: 'Apply Date',
          className: 'col-12',
          type: InputTypesEnum.Date,
        },
        {
          fieldName: 'Fee',
          label: (item) => `Fee (${item.FreelancerUserCurrencyCode})`,
          className: 'col-12',
          type: InputTypesEnum.NumberWithTwoDecimalPlaces,
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          validityChecker: ValidityHelper.forRequiredNumberFieldGreaterThanZero,
        },
        {
          fieldName: 'IsActive',
          label: 'Is Active?',
          className: 'col-12',
          type: InputTypesEnum.OnOff,
          validityChecker: ValidityHelper.forRequiredBoolField,
        },
        {
          fieldName: 'LastComment',
          label: 'Comment',
          className: 'col-12',
          type: InputTypesEnum.Text,
          hide: FreelancerLineItemWindowDescription.isTimesheet,
          validityChecker: ValidityHelper.forRequiredTextField,
        },
      ],
    },
  ];

  static getFeeTypeValues(item) {
    const values = [{ label: 'Choose Fee Type...', value: '', disabled: false }];
    if (item.FreelancerUserIsRM) values.push({ label: FeeTypesEnum.Resource, value: FeeTypesEnum.Resource });
    if (item.FreelancerUserIsDQ) values.push({ label: FeeTypesEnum.DataQuality, value: FeeTypesEnum.DataQuality });
    if (item.FreelancerUserIsKAM) values.push({ label: FeeTypesEnum.KAM, value: FeeTypesEnum.KAM });
    if (item.FreelancerUserIsBD) values.push({ label: FeeTypesEnum.BD, value: FeeTypesEnum.BD });
    if (item.FreelancerUserIsBI) values.push({ label: FeeTypesEnum.BI, value: FeeTypesEnum.BI });
    if (item.FreelancerUserIsIT) values.push({ label: FeeTypesEnum.IT, value: FeeTypesEnum.IT });
    if (item.FreelancerUserIsHR) values.push({ label: FeeTypesEnum.HR, value: FeeTypesEnum.HR });
    if (item.FreelancerUserIsMarketing)
      values.push({ label: FeeTypesEnum.Marketing, value: FeeTypesEnum.Marketing });
    if (item.FreelancerUserIsFinance) values.push({ label: FeeTypesEnum.Finance, value: FeeTypesEnum.Finance });
    if (item.FreelancerUserIsFieldworkPartner)
      values.push({ label: FeeTypesEnum.FieldworkPartner, value: FeeTypesEnum.FieldworkPartner });
    return values;
  }

  static onClientChange(item) {
    // Client drives the survey/wave filters, so clear the dependent selections when it changes.
    item.SurveyId = '';
    item.TpSurveyId = '';
    item.WaveId = '';
    item.TpWaveId = '';
  }

  static onSourceChange(item) {
    // Keep only the selection belonging to the current source so the payload never carries both.
    if (FreelancerLineItemWindowDescription.isThirdParty(item)) {
      item.SurveyId = '';
      item.WaveId = '';
    } else {
      item.TpSurveyId = '';
      item.TpWaveId = '';
    }
  }
}
